import { apiService } from "src/services";
import type { IDTProject, IResponse, UserAccessTypeString } from "src/pages/Studies/models/interfaces";
import type { ICreateDTProjectRequest } from "../models/interfaces";
import type { IMarketContextUploadPayload } from "src/pages/DTProjectDetail/models/interfaces";

/**
 * Fetch all DT projects for the current profile.
 */
export const fetchDTProjects = async (): Promise<{
  projects: IDTProject[];
}> => {
  const res = await apiService.get<{ data: { projects: IDTProject[] } }>(
    "/dt/projects"
  );
  return res.data;
};

/**
 * Create a new DT project.
 */
export const createDTProject = async (
  req: ICreateDTProjectRequest
): Promise<IDTProject> => {
  const res = await apiService.post<{ data: IDTProject }, ICreateDTProjectRequest>(
    "/dt/projects",
    req
  );
  return res.data;
};

/**
 * Delete a DT project by ID.
 */
export const deleteDTProjectApi = async (
  projectId: string
): Promise<{ ok: boolean }> => {
  const res = await apiService.delete<{ data: { ok: boolean } }>(
    `/dt/projects/${projectId}`
  );
  return res.data;
};

/**
 * Upload a ZIP file of JSON system prompts for a DT project.
 */
export const uploadDTProjectFile = async (
  projectId: string,
  file: File
): Promise<IDTProject> => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await apiService.post<{ data: IDTProject }, FormData>(
    `/dt/projects/${projectId}/upload`,
    formData
  );
  return res.data;
};

/**
 * Upload/replace the market-context JSON on a DT project. Parsed client-side
 * and POSTed as a JSON body; backend validates the shape.
 * Mirrors the helper in DTProjectDetail/serviceHelper so the project-creation
 * modal can attach a context at creation time.
 */
export const uploadDTMarketContextFromProjects = async (
  projectId: string,
  marketContext: IMarketContextUploadPayload
): Promise<IDTProject> => {
  const res = await apiService.post<{ data: IDTProject }, IMarketContextUploadPayload>(
    `/dt/projects/${projectId}/market-context`,
    marketContext
  );
  return res.data;
};

// ── Collaborator API calls ──────────────────────────────────────

/**
 * Fetch all profile users for the collaborator modal.
 */
export const getDTAllUsers = async (projectId: string): Promise<IResponse> => {
  return await apiService.get(`/dt/projects/${projectId}/allusers`);
};

/**
 * Add, update, or remove a collaborator on a DT project.
 */
export const addDTCollaborator = async (projectId: string, values: {
  collaborator_id: string;
  collaborator_name: string;
  collaborator_email: string;
  user_access_type: UserAccessTypeString;
  is_owner: boolean;
}): Promise<IResponse> => {
  return await apiService.post(
    `/dt/projects/${projectId}/collaborator`,
    values
  );
};

/**
 * Transfer DT project ownership to another Full Access collaborator.
 */
export const transferDTProjectOwner = async (projectId: string, values: {
  collaborator_id: string;
  collaborator_name: string;
  collaborator_email: string;
}): Promise<IResponse> => {
  return await apiService.post(
    `/dt/projects/${projectId}/collaborator/owner`,
    values
  );
};
